import { z } from 'zod';
import { ConnectedClientAccessSchema } from '../identity/connected-clients.schemas';
import { McpCredentialSeatSchema } from './mcp-access.schemas';

/**
 * The operator-tier read over the OAuth grants customers' MCP clients hold
 * (IMM-625.4).
 *
 * **A deliberate widening of the member's own list, stated as a delta** (BL-092).
 * `contracts.connectedClients.list` serves the CALLER's own grants, ACTIVE only,
 * newest-connection first. This one serves every workspace, lets `status` select
 * revoked grants too — a revoked grant is exactly what a support question is
 * often about — and orders by last use, because the operator is looking for the
 * grant that was just used. Every narrowing the member's list applies for
 * privacy (no token, no token prefix, no issuer grant id) is kept. The
 * `ALL_TOOLS → []` projection of `grantedPermissions` is the same as the
 * member's, so that delta is none.
 *
 * It is a READ. Revoking a grant stays the member's own screen; nothing here
 * writes.
 */
export const AdminConnectedClientStatusSchema = z.enum(['active', 'revoked']);
export type AdminConnectedClientStatus = z.infer<typeof AdminConnectedClientStatusSchema>;

export const ListAdminConnectedClientsQuerySchema = z.object({
  /**
   * Narrows to grants whose bound user holds a seat in this workspace — NOT to
   * the grant's own `workspace_id`, which records only where consent was given.
   * Filtering on the stored column would hide a grant that reaches the workspace
   * the operator asked about, which is the question being asked.
   */
  workspaceId: z.uuid().optional(),
  userId: z.uuid().optional(),
  status: AdminConnectedClientStatusSchema.optional(),
});
export type ListAdminConnectedClientsQuery = z.infer<typeof ListAdminConnectedClientsQuerySchema>;

/**
 * One grant as the operator sees it.
 *
 * `seats` is resolved from the bound USER's active member rows — the same source
 * `ResolveOauthPrincipal` reads per request — so the picker can never offer a
 * seat the surface would not honour. It is empty when the person holds no active
 * seat at all, which is itself the answer to „why can this client do nothing".
 *
 * `userEmail` is LEFT-joined for display and is null when the user row has since
 * gone; `userId` is always retained.
 */
export const AdminConnectedClientItemSchema = z.object({
  id: z.uuid(),
  clientName: z.string(),
  /**
   * A bare string, NOT a uuid (BL-039). `connected_clients.user_id` is nullable
   * in the column — it was added to a table that could already hold IMM-601.8.1
   * rows — and the repository hydrates such a row's user id as `''`. A uuid
   * schema would fail the WHOLE list on one legacy grant, which is exactly the
   * grant an operator would be diagnosing.
   */
  userId: z.string(),
  userEmail: z.string().nullable(),
  status: AdminConnectedClientStatusSchema,
  /**
   * Whether the grant is unscoped (`ALL_TOOLS`, follows the catalogue) or scoped.
   * An enum, unlike `grantedPermissions`, because the column is NOT NULL with a
   * CHECK on exactly these literals (IMM-1094.1), so no stored row can fail it
   * (BL-039). Under `ALL_TOOLS`, `grantedPermissions` is `[]`, the member's own
   * projection: the stored list is only a revert fallback, never the ceiling.
   */
  access: ConnectedClientAccessSchema,
  /**
   * Bare strings, like the member's own list and `AdminApiKeyView` (BL-039):
   * `granted_permissions` is a `text[]` column, so a row can legitimately hold a
   * literal the current `PERMISSIONS` tuple no longer contains — a permission
   * renamed or retired after the grant was consented. An enum here would fail
   * the whole list on exactly the stale grant an operator came to look at.
   */
  grantedPermissions: z.array(z.string()),
  seats: z.array(McpCredentialSeatSchema),
  rateLimit: z.number().int(),
  dailyCallLimit: z.number().int(),
  lastUsedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type AdminConnectedClientItem = z.infer<typeof AdminConnectedClientItemSchema>;

export const ListAdminConnectedClientsResponseSchema = z.object({
  items: z.array(AdminConnectedClientItemSchema),
});
export type ListAdminConnectedClientsResponse = z.infer<typeof ListAdminConnectedClientsResponseSchema>;
